import { useEffect, useMemo, useState } from 'react';
import { Topbar } from '@/components/Topbar';
import { Button } from '@/components/Button';
import { Card, SectionHeader } from '@/components/Card';
import { Modal } from '@/components/Modal';
import { FormField } from '@/components/FormField';
import { EmptyState } from '@/components/EmptyState';
import {
  emailSequencesService,
  EmailSequence,
  EmailSequenceStep,
  EmailSequenceEnrolment,
  CreateSequenceInput,
} from '@/services/email-sequences.service';
import { contactGroupsService, ContactGroup } from '@/services/contact-groups.service';
import { api } from '@/config/api';
import {
  Mail, Plus, Pause, Play, Trash2, ChevronUp, ChevronDown, Send,
  Users, Clock, MailCheck, MessageSquareReply, X, GripVertical,
} from 'lucide-react';
import clsx from 'clsx';
import toast from 'react-hot-toast';

const inputCls =
  'w-full rounded-xl border border-border bg-surface px-3 py-2 text-sm text-text-primary placeholder:text-text-muted focus:outline-none focus:ring-2 focus:ring-primary/40 focus:border-primary';

interface EmailTemplate { templateUid: string; name: string; subject: string; isActive: boolean; }

const STATUS_PILL: Record<EmailSequence['status'], string> = {
  active: 'bg-success-soft text-success',
  paused: 'bg-warning-soft text-warning',
  draft:  'bg-surface-alt text-text-muted',
};

const ENROLMENT_STATUS_PILL: Record<EmailSequenceEnrolment['status'], string> = {
  active:    'bg-info-soft text-info',
  paused:    'bg-warning-soft text-warning',
  completed: 'bg-success-soft text-success',
  cancelled: 'bg-surface-alt text-text-muted',
  replied:   'bg-primary-soft text-primary',
  bounced:   'bg-danger-soft text-danger',
};

export default function EmailSequences() {
  const [sequences, setSequences] = useState<EmailSequence[] | null>(null);
  const [editorOpen, setEditorOpen] = useState(false);
  const [editing, setEditing] = useState<EmailSequence | null>(null);
  const [enrolFor, setEnrolFor] = useState<EmailSequence | null>(null);
  const [viewing, setViewing] = useState<EmailSequence | null>(null);

  const refresh = () => emailSequencesService.list().then(setSequences).catch(() => setSequences([]));
  useEffect(() => { refresh(); }, []);

  const onSave = async () => { await refresh(); };

  const toggleStatus = async (seq: EmailSequence) => {
    const next = seq.status === 'active' ? 'paused' : 'active';
    try {
      await emailSequencesService.update(seq.sequenceUid, { status: next });
      toast.success(`Sequence ${next}`);
      await refresh();
    } catch { /* axios toasts */ }
  };

  const onDelete = async (seq: EmailSequence) => {
    // eslint-disable-next-line no-restricted-globals, no-alert
    if (!window.confirm(`Delete "${seq.name}"? Active enrolments will be cancelled.`)) return;
    try {
      await emailSequencesService.remove(seq.sequenceUid);
      toast.success('Sequence deleted');
      await refresh();
    } catch { /* axios toasts */ }
  };

  return (
    <div>
      <Topbar
        title="Email sequences"
        breadcrumb="Automation"
        description="Multi-step drip campaigns with auto-pause on reply. Sequences send via your default email account."
        right={
          <Button onClick={() => { setEditing(null); setEditorOpen(true); }}>
            <Plus size={14} /> New sequence
          </Button>
        }
      />

      <div className="w-full px-7 py-7 space-y-4">
        {sequences === null && <Card><div className="skeleton h-24 rounded-lg" /></Card>}

        {sequences && sequences.length === 0 && (
          <EmptyState
            icon={<Mail size={40} className="text-text-muted" />}
            title="No sequences yet"
            description="Create your first multi-step drip — e.g. 'Welcome' (day 0) → 'Are you stuck?' (day 3) → 'Last check-in' (day 7). Replies auto-pause the sequence."
            action={
              <Button onClick={() => { setEditing(null); setEditorOpen(true); }}>
                <Plus size={14} /> New sequence
              </Button>
            }
          />
        )}

        {sequences && sequences.length > 0 && (
          <div className="space-y-2">
            {sequences.map((s) => (
              <Card key={s.sequenceUid} padded={false} className="p-4">
                <div className="flex items-start gap-3">
                  <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-primary-soft text-primary">
                    <Mail size={18} />
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="flex flex-wrap items-center gap-2">
                      <span className="font-display text-sm font-semibold text-text-primary">{s.name}</span>
                      <span className={clsx('rounded-full px-2 py-0.5 text-2xs font-semibold', STATUS_PILL[s.status])}>
                        {s.status}
                      </span>
                      <span className="rounded-full bg-surface-alt px-2 py-0.5 text-2xs text-text-muted">
                        {s.steps.length} step{s.steps.length === 1 ? '' : 's'}
                      </span>
                      {s.pauseOnReply && (
                        <span className="inline-flex items-center gap-1 rounded-full bg-info-soft px-2 py-0.5 text-2xs text-info">
                          <MessageSquareReply size={9} /> auto-pause on reply
                        </span>
                      )}
                    </div>
                    {s.description && (
                      <p className="mt-0.5 text-xs text-text-secondary">{s.description}</p>
                    )}
                    <div className="mt-1 flex flex-wrap items-center gap-3 text-2xs text-text-muted">
                      <span className="inline-flex items-center gap-1"><Users size={10} /> {s.totalEnrolled} enrolled</span>
                      <span className="inline-flex items-center gap-1"><MailCheck size={10} /> {s.totalSent} sent</span>
                      <span className="inline-flex items-center gap-1"><MessageSquareReply size={10} /> {s.totalReplied} replied</span>
                      <span className="inline-flex items-center gap-1"><Clock size={10} /> {s.totalCompleted} completed</span>
                    </div>
                  </div>
                  <div className="flex shrink-0 items-center gap-1">
                    <button
                      onClick={() => setViewing(s)}
                      className="rounded-lg border border-border px-2.5 py-1 text-xs hover:bg-surface-alt"
                      title="View enrolments"
                    >
                      Enrolments
                    </button>
                    <button
                      onClick={() => setEnrolFor(s)}
                      disabled={s.status !== 'active' || s.steps.length === 0}
                      className="rounded-lg border border-border bg-primary-soft px-2.5 py-1 text-xs font-semibold text-primary hover:bg-primary-soft/70 disabled:opacity-40"
                      title={s.status !== 'active' ? 'Activate the sequence first' : 'Enrol contacts'}
                    >
                      Enrol
                    </button>
                    <button
                      onClick={() => toggleStatus(s)}
                      className="rounded-lg p-1.5 text-text-muted hover:bg-surface-alt hover:text-text-primary"
                      title={s.status === 'active' ? 'Pause' : 'Activate'}
                    >
                      {s.status === 'active' ? <Pause size={14} /> : <Play size={14} />}
                    </button>
                    <button
                      onClick={() => { setEditing(s); setEditorOpen(true); }}
                      className="rounded-lg border border-border px-2.5 py-1 text-xs hover:bg-surface-alt"
                    >
                      Edit
                    </button>
                    <button
                      onClick={() => onDelete(s)}
                      className="rounded-lg p-1.5 text-text-muted hover:bg-danger-soft hover:text-danger"
                      title="Delete"
                    >
                      <Trash2 size={14} />
                    </button>
                  </div>
                </div>
              </Card>
            ))}
          </div>
        )}
      </div>

      {editorOpen && (
        <SequenceEditor
          existing={editing}
          onClose={() => setEditorOpen(false)}
          onSaved={() => { setEditorOpen(false); onSave(); }}
        />
      )}
      {enrolFor && (
        <EnrolModal
          sequence={enrolFor}
          onClose={() => setEnrolFor(null)}
          onEnrolled={() => { setEnrolFor(null); refresh(); }}
        />
      )}
      {viewing && (
        <EnrolmentsModal
          sequence={viewing}
          onClose={() => setViewing(null)}
        />
      )}
    </div>
  );
}

// ─────────────────────────── Sequence editor ───────────────────────────

const SequenceEditor: React.FC<{
  existing: EmailSequence | null;
  onClose: () => void;
  onSaved: () => void;
}> = ({ existing, onClose, onSaved }) => {
  const [name, setName] = useState(existing?.name ?? '');
  const [description, setDescription] = useState(existing?.description ?? '');
  const [pauseOnReply, setPauseOnReply] = useState(existing?.pauseOnReply ?? true);
  const [steps, setSteps] = useState<Array<EmailSequenceStep | Omit<EmailSequenceStep, 'stepUid' | 'order'>>>(
    existing?.steps ?? [{ delayDays: 0, templateUid: '', subject: '', bodyHtml: '', bodyText: '' } as Omit<EmailSequenceStep, 'stepUid' | 'order'>],
  );
  const [templates, setTemplates] = useState<EmailTemplate[]>([]);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api.get('/slighter_crm/email/templates').then((r) => setTemplates(r.data ?? [])).catch(() => setTemplates([]));
  }, []);

  const updateStep = (i: number, patch: Partial<EmailSequenceStep>) => {
    setSteps((prev) => prev.map((s, idx) => idx === i ? { ...s, ...patch } : s));
  };
  const removeStep = (i: number) => {
    if (steps.length <= 1) { toast.error('A sequence needs at least one step'); return; }
    setSteps((prev) => prev.filter((_, idx) => idx !== i));
  };
  const moveStep = (i: number, dir: -1 | 1) => {
    const next = i + dir;
    if (next < 0 || next >= steps.length) return;
    setSteps((prev) => {
      const out = [...prev];
      [out[i], out[next]] = [out[next], out[i]];
      return out;
    });
  };
  const addStep = () => {
    setSteps((prev) => [...prev, { delayDays: 1, templateUid: '', subject: '', bodyHtml: '', bodyText: '' }]);
  };

  const save = async () => {
    if (!name.trim()) { toast.error('Name is required'); return; }
    if (steps.length === 0) { toast.error('Add at least one step'); return; }
    for (let i = 0; i < steps.length; i++) {
      const s = steps[i];
      const hasTemplate = !!s.templateUid;
      const hasInline = !!(s.subject && s.bodyHtml);
      if (!hasTemplate && !hasInline) {
        toast.error(`Step ${i + 1}: pick a template or fill subject + body HTML`);
        return;
      }
    }
    setBusy(true);
    try {
      const payload: CreateSequenceInput = {
        name: name.trim(),
        description: description.trim() || undefined,
        pauseOnReply,
        steps: steps.map((s) => ({
          delayDays: Number(s.delayDays) || 0,
          templateUid: s.templateUid || undefined,
          subject: s.subject || undefined,
          bodyHtml: s.bodyHtml || undefined,
          bodyText: s.bodyText || undefined,
        })),
      };
      if (existing) {
        await emailSequencesService.update(existing.sequenceUid, payload);
        toast.success('Sequence saved');
      } else {
        await emailSequencesService.create(payload);
        toast.success('Sequence created — set status to Active to start enrolling');
      }
      onSaved();
    } catch { /* axios toasts */ } finally { setBusy(false); }
  };

  return (
    <Modal open onClose={() => !busy && onClose()} title={existing ? `Edit · ${existing.name}` : 'New email sequence'} widthClass="max-w-3xl">
      <div className="space-y-4">
        <div className="grid gap-3 sm:grid-cols-2">
          <FormField label="Name" required>
            <input className={inputCls} value={name} onChange={(e) => setName(e.target.value)} placeholder="Welcome drip — 7 days" />
          </FormField>
          <FormField label="Auto-pause on reply" hint="When the contact replies via email, the sequence auto-pauses for them.">
            <label className="flex items-center gap-2 rounded-xl border border-border bg-surface px-3 py-2 text-sm cursor-pointer">
              <input type="checkbox" className="accent-primary" checked={pauseOnReply} onChange={(e) => setPauseOnReply(e.target.checked)} />
              {pauseOnReply ? 'Yes — pause on inbound email' : 'No — keep sending regardless'}
            </label>
          </FormField>
        </div>

        <FormField label="Description (optional)">
          <textarea className={clsx(inputCls, 'resize-none')} rows={2} value={description} onChange={(e) => setDescription(e.target.value)} />
        </FormField>

        <div>
          <SectionHeader title="Steps" subtitle="Each step waits N days from the previous send (or from enrolment for step 1)." />
          <div className="space-y-2">
            {steps.map((s, i) => (
              <div key={i} className="rounded-xl border border-border bg-surface-alt/40 p-3">
                <div className="flex items-start gap-2">
                  <div className="flex shrink-0 flex-col items-center rounded-lg bg-surface text-text-muted">
                    <button onClick={() => moveStep(i, -1)} disabled={i === 0} className="px-1 pt-1 hover:text-text-primary disabled:opacity-25"><ChevronUp size={14} /></button>
                    <GripVertical size={12} className="opacity-50" />
                    <button onClick={() => moveStep(i, 1)} disabled={i === steps.length - 1} className="px-1 pb-1 hover:text-text-primary disabled:opacity-25"><ChevronDown size={14} /></button>
                  </div>
                  <div className="flex-1 space-y-2">
                    <div className="flex items-center gap-2">
                      <span className="rounded-md bg-primary-soft px-2 py-0.5 text-2xs font-semibold text-primary">Step {i + 1}</span>
                      <FormField label="Wait (days)">
                        <input
                          type="number"
                          min={0}
                          className={clsx(inputCls, 'w-24')}
                          value={String(s.delayDays ?? 0)}
                          onChange={(e) => updateStep(i, { delayDays: parseInt(e.target.value, 10) || 0 })}
                        />
                      </FormField>
                      <span className="text-2xs text-text-muted">
                        {i === 0 ? 'after enrolment' : `after step ${i}`}
                      </span>
                    </div>

                    <FormField label="Template">
                      <select
                        className={inputCls}
                        value={s.templateUid ?? ''}
                        onChange={(e) => updateStep(i, { templateUid: e.target.value || undefined })}
                      >
                        <option value="">— Inline (fill subject + body below) —</option>
                        {templates.filter((t) => t.isActive).map((t) => (
                          <option key={t.templateUid} value={t.templateUid}>{t.name} · {t.subject}</option>
                        ))}
                      </select>
                    </FormField>

                    {!s.templateUid && (
                      <>
                        <FormField label="Subject" required>
                          <input
                            className={inputCls}
                            value={s.subject ?? ''}
                            onChange={(e) => updateStep(i, { subject: e.target.value })}
                            placeholder="e.g. Quick check-in"
                          />
                        </FormField>
                        <FormField label="Body (HTML)" required hint="Plain text fallback is auto-generated if blank.">
                          <textarea
                            rows={4}
                            className={clsx(inputCls, 'font-mono text-xs')}
                            value={s.bodyHtml ?? ''}
                            onChange={(e) => updateStep(i, { bodyHtml: e.target.value })}
                            placeholder="<p>Hi {{name}}…</p>"
                          />
                        </FormField>
                      </>
                    )}
                  </div>
                  <button
                    onClick={() => removeStep(i)}
                    className="rounded-lg p-1.5 text-text-muted hover:bg-danger-soft hover:text-danger"
                    title="Remove step"
                  >
                    <X size={14} />
                  </button>
                </div>
              </div>
            ))}
          </div>
          <Button size="sm" variant="ghost" iconLeft={<Plus size={13} />} onClick={addStep}>Add step</Button>
        </div>

        <div className="flex items-center justify-end gap-2 border-t border-border pt-4">
          <Button variant="ghost" onClick={onClose} disabled={busy}>Cancel</Button>
          <Button variant="primary" onClick={save} loading={busy}>{existing ? 'Save changes' : 'Create sequence'}</Button>
        </div>
      </div>
    </Modal>
  );
};

// ─────────────────────────── Enrol modal ───────────────────────────────

const EnrolModal: React.FC<{
  sequence: EmailSequence;
  onClose: () => void;
  onEnrolled: () => void;
}> = ({ sequence, onClose, onEnrolled }) => {
  const [mode, setMode] = useState<'group' | 'emails'>('group');
  const [groups, setGroups] = useState<ContactGroup[]>([]);
  const [groupUid, setGroupUid] = useState('');
  const [emailsRaw, setEmailsRaw] = useState('');
  const [busy, setBusy] = useState(false);

  useEffect(() => { contactGroupsService.list().then(setGroups).catch(() => setGroups([])); }, []);

  const enrol = async () => {
    setBusy(true);
    try {
      let r: { enrolled: number; skipped: number };
      if (mode === 'group') {
        if (!groupUid) { toast.error('Pick a group'); setBusy(false); return; }
        r = await emailSequencesService.enrolFromGroup(sequence.sequenceUid, groupUid);
      } else {
        const recipients = emailsRaw
          .split(/[\n,;]+/)
          .map((line) => line.trim())
          .filter(Boolean)
          .map((email) => ({ email }));
        if (recipients.length === 0) { toast.error('Add at least one email'); setBusy(false); return; }
        r = await emailSequencesService.enrolByEmails(sequence.sequenceUid, recipients);
      }
      toast.success(`Enrolled ${r.enrolled}${r.skipped ? ` · skipped ${r.skipped}` : ''}`);
      onEnrolled();
    } catch { /* axios */ } finally { setBusy(false); }
  };

  return (
    <Modal open onClose={() => !busy && onClose()} title={`Enrol contacts · ${sequence.name}`} widthClass="max-w-lg">
      <div className="space-y-4">
        <div className="rounded-xl border border-border bg-surface-alt/50 p-3 text-xs text-text-secondary">
          Enrolees receive step 1 after its <strong>{sequence.steps[0]?.delayDays ?? 0} day</strong> wait elapses.
          The next-send timer is exact; the worker tick that triggers sends runs every 30 minutes.
        </div>
        <div className="flex gap-2">
          <button
            onClick={() => setMode('group')}
            className={clsx('flex-1 rounded-xl border px-3 py-2 text-sm', mode === 'group' ? 'border-primary bg-primary-soft text-primary' : 'border-border bg-surface text-text-primary')}
          >From contact group</button>
          <button
            onClick={() => setMode('emails')}
            className={clsx('flex-1 rounded-xl border px-3 py-2 text-sm', mode === 'emails' ? 'border-primary bg-primary-soft text-primary' : 'border-border bg-surface text-text-primary')}
          >Paste emails</button>
        </div>

        {mode === 'group' ? (
          <FormField label="Contact group" required>
            <select className={inputCls} value={groupUid} onChange={(e) => setGroupUid(e.target.value)}>
              <option value="">— Pick a group —</option>
              {groups.map((g) => <option key={g.groupUid} value={g.groupUid}>{g.name} ({g.contactCount ?? 0} contacts)</option>)}
            </select>
          </FormField>
        ) : (
          <FormField label="Email addresses" required hint="One per line, or comma-separated">
            <textarea
              rows={6}
              className={clsx(inputCls, 'font-mono text-xs')}
              value={emailsRaw}
              onChange={(e) => setEmailsRaw(e.target.value)}
              placeholder={'alice@example.com\nbob@example.com'}
            />
          </FormField>
        )}

        <div className="flex items-center justify-end gap-2 border-t border-border pt-4">
          <Button variant="ghost" onClick={onClose} disabled={busy}>Cancel</Button>
          <Button variant="primary" onClick={enrol} loading={busy}><Send size={13} /> Enrol</Button>
        </div>
      </div>
    </Modal>
  );
};

// ─────────────────────────── Enrolments viewer ─────────────────────────

const EnrolmentsModal: React.FC<{
  sequence: EmailSequence;
  onClose: () => void;
}> = ({ sequence, onClose }) => {
  const [rows, setRows] = useState<EmailSequenceEnrolment[] | null>(null);

  const refresh = () => emailSequencesService.listEnrolments(sequence.sequenceUid).then(setRows).catch(() => setRows([]));
  useEffect(() => { refresh(); }, []);

  const counts = useMemo(() => {
    const c: Record<string, number> = {};
    (rows ?? []).forEach((r) => { c[r.status] = (c[r.status] ?? 0) + 1; });
    return c;
  }, [rows]);

  const onEnrolmentAction = async (e: EmailSequenceEnrolment, action: 'pause' | 'resume' | 'cancel') => {
    try {
      if (action === 'pause') await emailSequencesService.pauseEnrolment(e.enrolmentUid);
      if (action === 'resume') await emailSequencesService.resumeEnrolment(e.enrolmentUid);
      if (action === 'cancel') await emailSequencesService.cancelEnrolment(e.enrolmentUid);
      toast.success('Updated');
      refresh();
    } catch { /* axios */ }
  };

  return (
    <Modal open onClose={onClose} title={`Enrolments · ${sequence.name}`} widthClass="max-w-3xl">
      <div className="space-y-3">
        <div className="flex flex-wrap gap-2 text-xs">
          {Object.entries(counts).map(([status, n]) => (
            <span key={status} className={clsx('rounded-full px-2 py-0.5', ENROLMENT_STATUS_PILL[status as EmailSequenceEnrolment['status']] ?? 'bg-surface-alt text-text-muted')}>
              {n} {status}
            </span>
          ))}
        </div>

        {rows === null && <div className="skeleton h-24 rounded-lg" />}
        {rows && rows.length === 0 && (
          <div className="rounded-xl border border-dashed border-border bg-surface-alt/40 p-8 text-center text-sm text-text-muted">
            No enrolments yet — use the Enrol button on the sequence row to add contacts.
          </div>
        )}

        {rows && rows.length > 0 && (
          <div className="overflow-hidden rounded-xl border border-border">
            <table className="w-full text-xs">
              <thead className="bg-surface-alt/60 text-2xs uppercase tracking-wide text-text-muted">
                <tr>
                  <th className="px-3 py-2 text-left">Recipient</th>
                  <th className="px-3 py-2 text-left">Status</th>
                  <th className="px-3 py-2 text-right">Step</th>
                  <th className="px-3 py-2 text-right">Next send</th>
                  <th className="px-3 py-2"></th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border">
                {rows.map((e) => (
                  <tr key={e.enrolmentUid} className="hover:bg-surface-alt">
                    <td className="px-3 py-2">
                      <div className="font-medium text-text-primary">{e.recipientName ?? e.recipientEmail}</div>
                      {e.recipientName && <div className="text-2xs text-text-muted">{e.recipientEmail}</div>}
                    </td>
                    <td className="px-3 py-2">
                      <span className={clsx('rounded-full px-2 py-0.5 text-2xs font-semibold', ENROLMENT_STATUS_PILL[e.status])}>
                        {e.status}
                      </span>
                    </td>
                    <td className="px-3 py-2 text-right tabular-nums">
                      {e.currentStepIndex + 1} / {sequence.steps.length}
                    </td>
                    <td className="px-3 py-2 text-right text-text-muted">
                      {e.nextSendAt ? new Date(e.nextSendAt).toLocaleString() : '—'}
                    </td>
                    <td className="px-3 py-2 text-right">
                      <div className="flex justify-end gap-1">
                        {e.status === 'active' && (
                          <button onClick={() => onEnrolmentAction(e, 'pause')} className="rounded p-1 text-text-muted hover:bg-warning-soft hover:text-warning" title="Pause">
                            <Pause size={12} />
                          </button>
                        )}
                        {(e.status === 'paused' || e.status === 'replied') && (
                          <button onClick={() => onEnrolmentAction(e, 'resume')} className="rounded p-1 text-text-muted hover:bg-success-soft hover:text-success" title="Resume">
                            <Play size={12} />
                          </button>
                        )}
                        {e.status !== 'cancelled' && e.status !== 'completed' && (
                          <button onClick={() => onEnrolmentAction(e, 'cancel')} className="rounded p-1 text-text-muted hover:bg-danger-soft hover:text-danger" title="Cancel">
                            <X size={12} />
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </Modal>
  );
};
